import { useCallback, useState } from 'react';
import { Button, Input, Card, Badge } from '@/components/ui';
import { LogOut } from 'lucide-react';
import { AdminMetrics } from './AdminMetrics';
import { AdminUsers } from './AdminUsers';
import { AdminSms } from './AdminSms';
import { AdminLlmRuns } from './AdminLlmRuns';
import { AdminLlmSpend } from './AdminLlmSpend';
import { AdminAccuracy } from './AdminAccuracy';
import { AdminFamilyContext } from './AdminFamilyContext';
import { AdminPostHog } from './AdminPostHog';
import { AdminSentry } from './AdminSentry';

type Tab =
  | 'metrics'
  | 'users'
  | 'sms'
  | 'llm-runs'
  | 'llm-spend'
  | 'accuracy'
  | 'family-context'
  | 'posthog'
  | 'sentry';

const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL ?? 'http://127.0.0.1:54321';
const ADMIN_USERS_URL = `${supabaseUrl}/functions/v1/admin-users`;

export function AdminShell() {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('metrics');
  const [submitting, setSubmitting] = useState(false);

  const handleLogin = useCallback(async () => {
    if (!password) return;
    setSubmitting(true);
    setAuthError(null);
    try {
      // Re-use admin-users as the credential check — cheapest endpoint.
      const res = await fetch(ADMIN_USERS_URL, {
        method: 'GET',
        headers: { 'X-Admin-Password': password },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Invalid admin password');
      }
      setAuthenticated(true);
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setSubmitting(false);
    }
  }, [password]);

  const handleLogout = useCallback(() => {
    setAuthenticated(false);
    setPassword('');
    setTab('metrics');
  }, []);

  if (!authenticated) {
    return (
      <div className="min-h-dvh flex items-center justify-center p-4 bg-muted">
        <Card
          variant="elevated"
          padding="lg"
          className="w-full max-w-sm"
        >
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-primary">YARUTA</h1>
            <p className="text-xs text-muted-foreground mt-1">Admin Panel</p>
          </div>
          <div className="flex flex-col gap-4">
            <Input
              type="password"
              placeholder="Admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleLogin();
              }}
            />
            {authError ? (
              <div className="px-4 py-3 bg-danger-light border border-danger rounded-md text-danger text-sm">
                {authError}
              </div>
            ) : null}
            <Button
              onClick={handleLogin}
              fullWidth
              loading={submitting}
            >
              Sign in
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-muted">
      <header className="bg-background border-b border-border sticky top-0 z-[100]">
        <div className="max-w-7xl mx-auto px-4 py-3 md:px-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-xl font-bold text-primary">YARUTA</h1>
              <p className="text-xs text-muted-foreground">Admin</p>
            </div>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setTab('metrics')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  tab === 'metrics'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                Metrics
              </button>
              <button
                type="button"
                onClick={() => setTab('users')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                  tab === 'users'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                Users
                <Badge
                  variant="info"
                  size="sm"
                >
                  approval
                </Badge>
              </button>
              <button
                type="button"
                onClick={() => setTab('sms')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  tab === 'sms'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                SMS
              </button>
              <button
                type="button"
                onClick={() => setTab('llm-runs')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  tab === 'llm-runs'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                LLM Stress Tests
              </button>
              <button
                type="button"
                onClick={() => setTab('llm-spend')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  tab === 'llm-spend'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                LLM Spend
              </button>
              <button
                type="button"
                onClick={() => setTab('accuracy')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  tab === 'accuracy'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                Accuracy
              </button>
              <button
                type="button"
                onClick={() => setTab('family-context')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  tab === 'family-context'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                Family Context
              </button>
              <button
                type="button"
                onClick={() => setTab('posthog')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  tab === 'posthog'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                PostHog
              </button>
              <button
                type="button"
                onClick={() => setTab('sentry')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  tab === 'sentry'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                Sentry
              </button>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            leftIcon={<LogOut size={14} />}
          >
            Log out
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-6">
        {tab === 'metrics' ? (
          <AdminMetrics password={password} />
        ) : tab === 'users' ? (
          <AdminUsers password={password} />
        ) : tab === 'sms' ? (
          <AdminSms password={password} />
        ) : tab === 'llm-runs' ? (
          <AdminLlmRuns password={password} />
        ) : tab === 'llm-spend' ? (
          <AdminLlmSpend password={password} />
        ) : tab === 'accuracy' ? (
          <AdminAccuracy password={password} />
        ) : tab === 'family-context' ? (
          <AdminFamilyContext password={password} />
        ) : tab === 'posthog' ? (
          <AdminPostHog />
        ) : (
          <AdminSentry password={password} />
        )}
      </main>
    </div>
  );
}
